import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  type RippleGlobalOptions,
} from '@angular/material/core';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from '@angular/material/dialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideMarkdown } from 'ngx-markdown';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { appRoutes } from 'app/app.routes';
import { configureCortexFunctionEditor } from 'app/core/cortex/monaco-function-editor.setup';
import { provideAuth } from 'app/core/auth/auth.provider';
import { sessionNavigationErrorHandler } from 'app/core/auth/session-navigation-error.handler';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideVersionCheck } from 'app/core/version/version-check.service';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21+ bootstraps zoneless unless told otherwise. The console still
    // relies on Zone.js to trigger change detection after async work, so keep it.
    provideZoneChangeDetection(),
    provideAnimations(),
    provideHttpClient(withXhr()),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),

      /**
       * A navigation that a signed-in session cannot complete is a sign-in
       * failure with nowhere to appear — the staging Fleet whitelist being the
       * case that made this visible. See the handler for what it does and, more
       * importantly, what it must not do.
       */
      withNavigationErrorHandler(sessionNavigationErrorHandler),
    ),

    /**
     * No ripples anywhere in the console.
     *
     * Material's ripple is a circle that expands from the pointer to cover the
     * control. On an icon button that circle is 40px wide and round, so in a
     * tight table cell it gets clipped by the cell edge and reads as a broken
     * half-moon — and it is the same effect that made the focus ring on the
     * dialog close buttons look wrong.
     *
     * Hover and focus feedback does not depend on it: Material draws those with
     * the persistent state layer, which stays. `styles.scss` reshapes that layer
     * for icon buttons from a circle into a rounded square so it sits inside its
     * cell.
     */
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: { disabled: true } satisfies RippleGlobalOptions,
    },

    /**
     * Every dialog dims and blurs the page behind it.
     *
     * `.console-dialog-backdrop` already existed, but as a convention: each call
     * site had to remember to pass it, and only three of eighteen did. So the
     * file editor blurred its background while "Create Deployment" — a far bigger
     * interruption — did not. A default cannot be forgotten; a convention can,
     * and this one was, fifteen times.
     *
     * Call sites that pass their own `backdropClass` still win, which is how the
     * What's New dialog keeps its grayscale variant.
     *
     * ## Why this spreads `new MatDialogConfig()`
     *
     * `MatDialog.open` merges as `{ ...(this._defaultOptions || new
     * MatDialogConfig()), ...config }` — so providing this token does not *add*
     * to Material's defaults, it **replaces** them. A bare
     * `{ backdropClass }` therefore left every other field `undefined` for every
     * dialog in the console, and the container reads several of them straight
     * off this object:
     *
     * - `autoFocus` fell from `'first-tabbable'` to `undefined`, which lands in
     *   the `default:` branch of the container's focus switch and calls
     *   `_focusByCssSelector(undefined)` — matching nothing. Nothing was
     *   focused on open and `cdkFocusInitial` was never consulted, anywhere.
     *   That is why several call sites pass an explicit `autoFocus`.
     * - `restoreFocus` fell from `true` to `undefined`, so closing a dialog did
     *   not return focus to whatever opened it.
     *
     * `hasBackdrop` and `closeOnNavigation` kept working, which is what made
     * this hard to spot: those are read from the CDK's *own* merged config,
     * which still gets `new DialogConfig()` because the CDK-level token is not
     * provided here.
     */
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        backdropClass: 'console-dialog-backdrop',
      } satisfies MatDialogConfig,
    },

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'tr',
            label: 'Turkish',
          },
        ],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      const defaultLang = translocoService.getDefaultLang();
      translocoService.setActiveLang(defaultLang);

      return firstValueFrom(translocoService.load(defaultLang));
    }),

    // Markdown rendering (used by online help system)
    provideMarkdown(),

    // Monaco Editor. `onMonacoLoad` runs once after the Monaco runtime is ready; we use
    // it to teach the TypeScript language service about the Cortex Serverless Function
    // authoring surface (handlers, events, context) so the function editor has typed
    // IntelliSense. See monaco-function-editor.setup.ts.
    provideMonacoEditor({
      baseUrl: './assets/monaco/min/vs',
      defaultOptions: {
        scrollBeyondLastLine: false,
        automaticLayout: true,
      },
      onMonacoLoad: () => configureCortexFunctionEditor(),
    }),

    // Fuse
    provideAuth(),
    provideIcons(),

    // Deploy detection — polls version.json and prompts a reload when the
    // deployed console no longer matches the running build.
    provideVersionCheck(),
    provideFuse({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      fuse: {
        layout: 'compact',
        scheme: 'dark',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-brand',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
